/**
 * Waitlist offer contracts (plan 2026-09-09-1309, U13 — R39, KTD10).
 *
 * A seat released with the `hold` disposition is offered to the oldest waiting
 * entry it fits. The offer is a capacity hold with an expiry; the person on the
 * waitlist claims it from a public page reached by the claim link in the offer
 * email (or a link an admin copied and sent by hand).
 *
 * The link carries the entry's public id and the entry's email. Both are
 * checked: a read or a claim whose email does not match the entry answers 404,
 * the same as an unknown entry, so the link cannot be probed for other people's
 * offers (the BE-TICKETS-002 posture the public order read already has).
 *
 * The claim itself posts the same body as the public order endpoint, to
 * `POST /v1/ticketed-events/by-slug/:schoolSlug/:eventPublicId/waitlist/:entryId/claim`,
 * so the public registration form is reused unchanged.
 */

import { z } from 'zod'

/** Path params shared by the offer read and the claim. */
export const PublicWaitlistOfferParamsSchema = z.object({
  schoolSlug: z.string().min(1),
  eventPublicId: z.string().min(1),
  entryId: z.string().min(1)
})
export type PublicWaitlistOfferParams = z.infer<typeof PublicWaitlistOfferParamsSchema>

/** `GET …/waitlist/:entryId?email=` — the entry's email is required. */
export const PublicWaitlistOfferQuerySchema = z.object({
  email: z.string().min(1)
})
export type PublicWaitlistOfferQuery = z.infer<typeof PublicWaitlistOfferQuerySchema>

/**
 * Where the offer stands, from the claimant's side:
 *   - `open`: an unexpired offer is waiting to be claimed.
 *   - `expired`: the claim window passed (whether or not the sweep has run yet).
 *   - `claimed`: the offer became a registration.
 *   - `unavailable`: no offer was ever made to this entry, or it was withdrawn.
 */
export const WaitlistOfferStateSchema = z.enum(['open', 'expired', 'claimed', 'unavailable'])
export type WaitlistOfferState = z.infer<typeof WaitlistOfferStateSchema>

export const PublicWaitlistOfferSchema = z.object({
  /** The waitlist entry's public id. */
  id: z.string(),
  state: WaitlistOfferStateSchema,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  /** When the claim window closes. Null when no offer was made. */
  expiresAt: z.string().nullable(),
  /** The ticket type the held seats are on. Null when no hold is open. */
  ticketTypeId: z.string().nullable(),
  /** Seats held for this entry; the claim may take at most this many from the hold. */
  quantity: z.number().int()
})
export type PublicWaitlistOffer = z.infer<typeof PublicWaitlistOfferSchema>

/** `POST /v1/ticketed-events/:eventPublicId/waitlist-entries/:entryId/cancel` (admin). */
export const CancelWaitlistEntryParamsSchema = z.object({
  eventPublicId: z.string().min(1),
  entryId: z.string().min(1)
})
export type CancelWaitlistEntryParams = z.infer<typeof CancelWaitlistEntryParamsSchema>
